import { CartAddEvent, ThemeEvents } from '@theme/events';
import { showCartFeedback } from '@theme/jd1-cart-feedback';

export const BUNDLE_DISCOUNT_TITLE = 'JD1 COMBO 5%';
let bundleBusy = false;
export function bundleMoneyFormatter(currency = 'COP', locale = 'es-CO') {
  if (currency === 'COP') {
    const numbers = new Intl.NumberFormat('es-CO', { useGrouping:true, minimumFractionDigits:0, maximumFractionDigits:0 });
    return { format: amount => `$${numbers.format(amount)}` };
  }
  return new Intl.NumberFormat(locale, { style:'currency', currency });
}
export function complementLimit(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 1 ? Math.min(2, Math.floor(parsed)) : 2;
}
export function bundleTotals(items) {
  const subtotal = items.reduce((sum, item) => sum + Number(item.price), 0);
  const saving = items.length > 1 ? items.reduce((sum, item) => sum + Math.round(Number(item.price) * .05), 0) : 0;
  return { subtotal, saving, total: subtotal - saving };
}
export function confirmedBundle(cart, group, expected) {
  const lines = (cart.items || []).filter(line => line.properties?._jd1_bundle === group);
  if (lines.length !== expected.length) return null;
  for (const item of expected) {
    const line = lines.find(line => String(line.variant_id) === String(item.id));
    if (!line || line.quantity !== 1 || Number(line.original_price) !== item.price || !Number.isFinite(Number(line.final_line_price))) return null;
    const allocation = (line.line_level_discount_allocations || []).filter(a => a.discount_application?.title === BUNDLE_DISCOUNT_TITLE);
    const saved = allocation.reduce((sum, a) => sum + Number(a.amount || 0), 0);
    if (saved <= 0 || Math.abs(saved - Math.round(item.price * .05)) > 1) return null;
    if (Number(line.final_line_price) > item.price - saved) return null;
  }
  return lines;
}
async function cartRequest(path, body) {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
    method: body ? 'POST' : 'GET', cache: 'no-store',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {})
  });
  const result = await response.json();
  if (!response.ok || result.status) throw new Error(result.description || result.message || 'No fue posible actualizar el carrito.');
  return result;
}
export async function commitBundle(expected, group, sections = []) {
  const items = expected.map((item, index) => ({ id: item.id, quantity: 1, properties: {
    _jd1_bundle: group, _jd1_bundle_role: index === 0 ? 'base' : 'complement'
  } }));
  let result;
  let addError;
  try {
    result = await cartRequest('cart/add.js', { items, sections, sections_url: window.location.pathname });
  } catch (error) { addError = error; }
  let cart;
  try { cart = await cartRequest('cart.js'); }
  catch (_) {
    const error = new Error('No pudimos confirmar el resultado. Revisa tu carrito antes de volver a agregar el combo.');
    error.uncertain = true; throw error;
  }
  const lines = confirmedBundle(cart, group, expected);
  if (lines) return { cart, lines, sections: result?.sections || {} };
  const ownLines = (cart.items || []).filter(line => line.properties?._jd1_bundle === group);
  if (ownLines.length) {
    try {
      cart = await cartRequest('cart/update.js', {
        updates: Object.fromEntries(ownLines.map(line => [line.key, 0])), sections, sections_url: window.location.pathname
      });
      if ((cart.items || []).some(line => line.properties?._jd1_bundle === group)) throw new Error();
    } catch (_) {
      const error = new Error('No pudimos validar el descuento ni retirar el combo. Revisa tu carrito antes de continuar.');
      error.uncertain = true; error.cart = cart; throw error;
    }
  }
  const error = new Error(addError?.message || 'El 5% no está disponible para esta selección. El combo no se ha añadido; puedes comprar los productos por separado.');
  error.cart = cart; throw error;
}
class JD1ProductBundle extends HTMLElement {
  connectedCallback() {
    this.abort?.abort(); this.abort = new AbortController();
    this.money = bundleMoneyFormatter(this.dataset.currency || 'COP', document.documentElement.lang || 'es-CO');
    this.addEventListener('change', event => this.onChange(event), {signal:this.abort.signal});
    this.querySelector('[data-bundle-submit]')?.addEventListener('click', () => this.addBundle(), {signal:this.abort.signal});
    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, event => {
      if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
      const incoming = Array.from(event.detail.data.html?.querySelectorAll('jd1-product-bundle') || []).find(node => node.dataset.blockId === this.dataset.blockId);
      const item = incoming?.querySelector('[data-current-product]');
      if (item) this.querySelector('[data-current-product]')?.replaceWith(document.importNode(item, true));
      else if (!event.detail.resource) this.querySelector('[data-current-product]').dataset.eligible = 'false';
      this.updateTotals();
    }, {signal:this.abort.signal});
    if (this.loaded) this.updateTotals(); else this.loadRecommendations();
  }
  disconnectedCallback() { this.abort?.abort(); }
  async loadRecommendations() {
    try {
      const response = await fetch(this.dataset.recommendationsUrl, {signal:this.abort.signal});
      if (!response.ok) throw new Error();
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = [...html.querySelectorAll('[data-bundle-recommendations] > [data-bundle-item]')]
        .filter(item => item.dataset.productId !== this.dataset.productId && item.dataset.eligible === 'true');
      const unique = items
        .filter((item, index) => items.findIndex(other => other.dataset.productId === item.dataset.productId) === index)
        .slice(0, complementLimit(this.dataset.maxComplements));
      if (!unique.length) {
        this.hidden = true;
        this.querySelector('[data-bundle-loading]').textContent = 'Asigna productos complementarios a este producto en Search & Discovery.';
        return;
      }
      this.querySelector('[data-bundle-complements]').replaceChildren(...unique.map(item => document.importNode(item, true)));
      const first = this.querySelector('[data-bundle-toggle]:not(:disabled)');
      if (!first) { this.hidden = true; return; }
      first.checked = true;
      this.querySelectorAll('[data-bundle-toggle]:not(:disabled)').forEach(toggle => { toggle.checked = true; });
      this.style.setProperty('--bundle-columns', String(unique.length + 1));
      this.loaded = true; this.hidden = false;
      this.querySelector('[data-bundle-loading]').hidden = true;
      this.querySelector('[data-bundle-footer]').hidden = false;
      this.updateTotals();
    } catch(error) {
      if (error.name === 'AbortError') return;
      this.hidden = true;
      this.querySelector('[data-bundle-loading]').textContent = 'No se pudieron cargar los complementarios.';
    }
  }
  onChange(event) {
    const select = event.target.closest('[data-bundle-variant]');
    if (select) {
      const item = select.closest('[data-bundle-item]');
      const variants = JSON.parse(item.querySelector('[data-bundle-variants]').textContent);
      const variant = variants.find(v => String(v.id) === select.value);
      const eligible = Boolean(variant?.available && variant.min <= 1 && variant.increment === 1 && !variant.requiresPlan);
      item.dataset.eligible = String(eligible);
      item.dataset.variantId = variant?.id || ''; item.dataset.price = String(variant?.price || 0);
      item.querySelector('[data-item-price]').textContent = this.format(variant?.price || 0);
      item.querySelector('[data-item-unavailable]').hidden = eligible;
      const toggle = item.querySelector('[data-bundle-toggle]'); toggle.disabled = !eligible;
      if (!eligible) toggle.checked = false;
      const img = item.querySelector('img');
      if (img && variant?.image) { img.src = variant.image; img.removeAttribute('srcset'); }
    }
    this.updateTotals();
  }
  selectedItems() {
    const items = [...this.querySelectorAll('[data-bundle-item]')];
    const main = items.find(item => item.hasAttribute('data-current-product'));
    const complements = items.filter(item => !item.hasAttribute('data-current-product') && item.querySelector('[data-bundle-toggle]')?.checked)
      .slice(0, complementLimit(this.dataset.maxComplements));
    return [...(main ? [main] : []), ...complements];
  }
  format(price) { return this.money.format(Number(price) / 100); }
  updateTotals() {
    const items = this.selectedItems();
    const totals = bundleTotals(items.map(item => ({price:Number(item.dataset.price)})));
    this.querySelector('[data-bundle-count]').textContent = String(items.length);
    this.querySelector('[data-bundle-subtotal]').textContent = this.format(totals.subtotal);
    this.querySelector('[data-bundle-subtotal]').hidden = !totals.saving;
    this.querySelector('[data-bundle-total]').textContent = this.format(totals.total);
    this.querySelector('[data-bundle-saving]').textContent = totals.saving ? `Ahorras ${this.format(totals.saving)} (5%)` : 'Selecciona un complemento para activar el combo.';
    this.querySelector('[data-bundle-submit]').disabled = this.busy || this.uncertain || !this.loaded || this.dataset.ready !== 'true' || items.length < 2 || items.some(item => item.dataset.eligible !== 'true');
  }
  publishCart(cart, sections = {}) {
    document.dispatchEvent(new CartAddEvent(cart, this.id, {source:'jd1-bundle',jd1DirectAdd:true,sections}));
  }
  async addBundle() {
    if (this.busy || bundleBusy || this.querySelector('[data-bundle-submit]').disabled) return;
    const selected = this.selectedItems();
    const expected = selected.map(item => ({id:item.dataset.variantId,price:Number(item.dataset.price)}));
    if (expected.length < 2 || expected.length > 3 || new Set(expected.map(item => item.id)).size !== expected.length) return;
    this.busy = true; bundleBusy = true; this.updateTotals();
    const button = this.querySelector('[data-bundle-submit]'); button.setAttribute('aria-busy','true');
    const message = this.querySelector('[data-bundle-message]'); message.textContent='Agregando combo…'; message.dataset.type='';
    const group = `jd1-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map(node => node.dataset.sectionId).filter(Boolean))].slice(0,5);
    let confirmedTotals;
    try {
      const result = await commitBundle(expected, group, sections);
      this.publishCart(result.cart, result.sections);
      const subtotal = result.lines.reduce((sum,line) => sum + Number(line.original_line_price),0);
      const total = result.lines.reduce((sum,line) => sum + Number(line.final_line_price),0);
      confirmedTotals = {subtotal,total};
      message.textContent=`Combo agregado con éxito. 5% aplicado: ${this.format(total)}.`;
      showCartFeedback({items:result.lines},this,{message:'Combo agregado con éxito · 5% aplicado',title:result.lines.map(line=>line.product_title || line.title).join(' + ')});
    } catch(error) {
      this.uncertain = Boolean(error.uncertain);
      if (error.cart) this.publishCart(error.cart,error.cart.sections || {});
      message.textContent=error.message; message.dataset.type='error';
    } finally {
      this.busy=false;bundleBusy=false;button.removeAttribute('aria-busy');this.updateTotals();
      if (confirmedTotals) {
        this.querySelector('[data-bundle-subtotal]').textContent=this.format(confirmedTotals.subtotal);
        this.querySelector('[data-bundle-total]').textContent=this.format(confirmedTotals.total);
        this.querySelector('[data-bundle-saving]').textContent=`Ahorras ${this.format(confirmedTotals.subtotal-confirmedTotals.total)}`;
      }
    }
  }
}
if (!customElements.get('jd1-product-bundle')) customElements.define('jd1-product-bundle',JD1ProductBundle);
